/**
 * Bulk Order Form JavaScript - Redesigned
 * Professional B2B Wholesale Ordering Interface
 * ================================================
 */

(function() {
  'use strict';

  // Namespace to avoid conflicts
  const BulkOrderForm = {
    config: {
      selectors: {
        section: '[data-section-type="bulk-order"]',
        workspace: '[data-bulk-order-products]',
        controls: '[data-sticky-controls]',
        productRow: '[data-product-row]',
        productsList: '[data-products-list]',
        qtyInput: '[data-qty-input]',
        qtyButton: '[data-qty-change]',
        submitButton: '[data-bulk-order-submit]',
        filterButton: '[data-collection-filter]',
        filterToggle: '[data-filters-toggle]',
        filterContent: '[data-filters-content]',
        filterCount: '[data-filter-count]',
        actionbar: '[data-actionbar]',
        selectedToggle: '[data-selected-toggle]',
        selectedPanel: '[data-selected-panel]',
        quoteDialog: '[data-quote-dialog]',
        quoteSend: '[data-quote-send]',
        quoteCancel: '[data-quote-cancel]',
        resumeDialog: '[data-resume-dialog]',
        resumeBody: '[data-resume-body]',
        resumeContinue: '[data-resume-continue]',
        resumeFresh: '[data-resume-fresh]',
        contactForm: '.bulk-order-contact__form',
        contactField: '[data-contact-field]',
        contactOrderBody: '[data-contact-order-body]',
        contactSuccess: '[data-contact-success]',
        contactServerErrors: '[data-contact-server-errors]',
        messages: '[data-bulk-order-messages]',
        summaryTotal: '[data-summary-total]',
        totalItems: '[data-total-items]',
        totalPieces: '[data-total-pieces]',
        selectedItemsList: '[data-selected-items-list]',
        productSearch: '[data-product-search]',
        collectionSearch: '[data-collection-search]',
        bulkSelectAll: '[data-bulk-select-all]',
        clearAll: '[data-clear-all]',
        clearDialog: '[data-clear-dialog]',
        clearConfirm: '[data-clear-confirm]',
        clearCancel: '[data-clear-cancel]',
        exportOrder: '[data-export-order]',
        saveDraft: '[data-save-draft]',
        lineSubtotal: '[data-line-subtotal]',
        emptyState: '[data-empty-state]',
        emptyOtherPages: '[data-empty-other-pages]',
        emptySearchStore: '[data-empty-search-store]',
        resetFilters: '[data-reset-filters]',
        reorderOpen: '[data-reorder-open]',
        reorderDialog: '[data-reorder-dialog]',
        catalogResults: '[data-catalog-results]'
      },
      searchDebounceDelay: 300, // milliseconds
      // Ceiling on a single Section Rendering request. Without one, a request
      // that is accepted and never answered suspends ensureFullCatalogLoaded
      // forever, so its finally never runs, the page-loader hold is never
      // released, and the full-screen overlay stays up for good.
      catalogFetchTimeoutMs: 20000,
      // Ceiling on the whole merge, which runs those requests in sequential
      // batches of three. MUST stay below HOLD_WATCHDOG_MS in
      // snippets/head.liquid (60000) — that is what guarantees this function's
      // finally re-enables the controls before the page loader gives up on the
      // hold and reveals the pad. Measured: ~15-22s for this 1,300-product
      // catalog on a wired connection, so this is headroom, not a target.
      catalogMergeTimeoutMs: 45000
    },

    state: {
      isPartner: false,
      currentFilter: 'all',
      selectedItems: [],
      totalAmount: 0,
      totalItems: 0,
      totalPieces: 0,
      storageKey: '',
      storageTtlMs: 30 * 24 * 60 * 60 * 1000,
      moneyFormat: '${{amount}}',
      strings: {},
      filtersExpanded: false,
      searchTimeout: null,
      collectionSearchTimeout: null,
      autosaveTimeout: null,
      orderCompleted: false,
      fullCatalogLoaded: false,
      // True once the saved draft is the "live" order (restored on load or
      // written this session); gates cross-page merging in save/getSelected.
      draftRestored: false
    },

    /**
     * Initialize the bulk order form
     */
    init: function() {
      const section = document.querySelector(this.config.selectors.section);
      if (!section) return;

      const partnerData = section.getAttribute('data-is-partner');
      this.state.isPartner = partnerData === 'true';
      this.configureStorage(section);

      // Get money format from section
      const moneyFormat = section.getAttribute('data-money-format');
      if (moneyFormat) {
        this.state.moneyFormat = moneyFormat;
      }

      // Load localized strings (rendered by the section via Shopify's | t)
      this.loadStrings(section);

      // Mirror responsive command-bar geometry into CSS and coordinate the
      // cross-fade with Broadcast's auto-hiding header.
      this.initStickyObserver();

      this.bindEvents();
      this.initializeQuantities();
      const contactResponseHandled = this.handleContactResponse();
      if (!contactResponseHandled) {
        this.maybeOfferResume();
      }
      this.updateSidebar();
      this.bindPaginationEvents();
      this.bindReorder();
      this.ensureFullCatalogLoaded();
    },

    /**
     * Scope unfinished orders to this shop and customer. Guests receive a
     * persistent browser identifier so closing the tab/browser and returning
     * in a later session still finds the same draft. A pre-upgrade tab-session
     * identifier is adopted on first load so an order already in progress is
     * not stranded. Legacy unscoped keys are removed because their ownership
     * cannot be established safely.
     */
    configureStorage: function(section) {
      const shop = section.getAttribute('data-shop-domain') || window.location.hostname;
      const customerId = section.getAttribute('data-customer-id');
      let actor = customerId ? `customer-${customerId}` : '';

      if (!actor) {
        const guestBrowserKey = 'hh_bulk_order_guest_browser';
        const legacyGuestSessionKey = 'hh_bulk_order_guest_session';
        const createGuestActor = () => {
          const randomId = (window.crypto && typeof window.crypto.randomUUID === 'function')
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          return `guest-${randomId}`;
        };

        try {
          actor = localStorage.getItem(guestBrowserKey) || '';
          if (!actor) {
            // Preserve a draft created by the previous session-scoped version.
            actor = sessionStorage.getItem(legacyGuestSessionKey) || createGuestActor();
            localStorage.setItem(guestBrowserKey, actor);
          }

          sessionStorage.removeItem(legacyGuestSessionKey);
        } catch (e) {
          // Private browsing can block localStorage. Retain same-tab recovery
          // as a graceful fallback even though cross-session recovery is then
          // unavailable.
          try {
            actor = sessionStorage.getItem(legacyGuestSessionKey) || createGuestActor();
            sessionStorage.setItem(legacyGuestSessionKey, actor);
          } catch (sessionError) {
            actor = createGuestActor();
          }
        }
      }

      this.state.storageKey = `bulk_order_quantities:v3:${encodeURIComponent(shop)}:${encodeURIComponent(actor)}`;

      try {
        localStorage.removeItem('bulk_order_quantities');
        localStorage.removeItem('bulk_order_draft');
      } catch (e) {
        // Storage can be unavailable in private browsing; the form still works.
      }
    },

    /**
     * Parse the localized strings JSON emitted by the section.
     * Falls back to English defaults if the script is missing.
     */
    loadStrings: function(section) {
      const fallback = {
        selected_n: 'Selected __n__ product(s)',
        confirm_clear: 'Are you sure you want to clear all quantities?',
        all_cleared: 'All quantities cleared',
        no_items_export: 'No items selected to export',
        exported: 'Order exported to CSV',
        no_items_save: 'No items to save',
        draft_saved_n: 'Draft saved with __n__ item(s)',
        could_not_save: 'Could not save draft',
        select_one: 'Please select at least one product with a quantity greater than 0.',
        added_n: 'Successfully added __n__ item(s) to your cart!',
        cart_error: 'There was an error adding items to your cart. Please try again.',
        fill_contact: 'Please fill out all required contact fields correctly.',
        quote_sent: 'Your quote request has been sent successfully! We will contact you shortly.',
        quote_error: 'There was an error sending your quote request. Please try again or contact us directly.',
        field_required: 'This field is required',
        invalid_email: 'Please enter a valid email address',
        invalid_phone: 'Please enter a valid phone number',
        more_n: '+ __n__ more item(s)',
        resume_n: 'You have an unfinished order with __n__ item(s). Continue where you left off?',
        no_items_selected: 'No items selected',
        catalog_load_error: 'The complete catalog could not be loaded. Please refresh the page and try again.',
        csv_product: 'Product',
        csv_variant: 'Variant',
        csv_sku: 'SKU',
        csv_barcode: 'Barcode',
        csv_qty_pcs: 'Qty (pcs)',
        csv_qty_cases: 'Qty (cases)',
        csv_total_pieces: 'Total pieces',
        csv_price: 'Price',
        csv_subtotal: 'Subtotal',
        quote_title: 'NEW BULK ORDER QUOTE REQUEST',
        quote_contact_heading: 'Contact information',
        quote_order_heading: 'Order details',
        quote_requested_on: 'Requested on',
        quote_company: 'Company',
        quote_name: 'Name',
        quote_email: 'Email',
        quote_phone: 'Phone',
        quote_sku: 'SKU',
        quote_barcode: 'Barcode',
        quote_quantity: 'Quantity',
        cases: 'cases',
        pcs: 'pcs'
      };

      let parsed = {};
      try {
        const el = (section || document).querySelector('[data-bulk-order-strings]');
        if (el && el.textContent.trim()) {
          parsed = JSON.parse(el.textContent);
        }
      } catch (e) {
        console.warn('Bulk order: could not parse localized strings, using defaults.', e);
      }

      this.state.strings = Object.assign({}, fallback, parsed);
    },

    /**
     * Look up a localized string, substituting the count placeholder.
     */
    t: function(key, count) {
      let str = this.state.strings[key] || key;
      if (count !== undefined && count !== null) {
        str = str.replace(/__n__/g, count);
      }
      return str;
    },

    /**
     * Flip `.is-stuck` on the section while the command bar is pinned, so the
     * bars can animate their elevation in/out. Scroll work is capped at one
     * rect read per animation frame. ResizeObserver keeps the table header's
     * top offset matched to the wrapped command bar at every breakpoint.
     * (IntersectionObserver proved unreliable alongside this theme's scripts.)
     */
    initStickyObserver: function() {
      const section = document.querySelector(this.config.selectors.section);
      const commandbar = document.querySelector('[data-commandbar]');
      if (!section || !commandbar) return;

      let wasStuck = false;
      let framePending = false;

      const syncGeometry = () => {
        const height = Math.ceil(commandbar.getBoundingClientRect().height);
        if (height <= 0) return;

        const value = `${height}px`;
        if (section.style.getPropertyValue('--bulk-commandbar-height') !== value) {
          section.style.setProperty('--bulk-commandbar-height', value);
        }
      };

      const check = () => {
        framePending = false;

        const commandbarStyle = window.getComputedStyle(commandbar);
        const stickyEnabled = commandbarStyle.position === 'sticky';
        const stickyTop = parseFloat(commandbarStyle.top) || 0;
        const stuck = stickyEnabled
          && window.scrollY > 0
          && commandbar.getBoundingClientRect().top <= stickyTop + 0.5;

        if (stuck !== wasStuck) {
          wasStuck = stuck;
          section.classList.toggle('is-stuck', stuck);
          // Body-level state hook. It styles nothing today: the bars dock
          // BELOW the theme header (--bulk-sticky-top) instead of hiding it,
          // so the rules that used to suppress the header are gone. Kept as
          // the one body-scoped signal that the order bars are pinned.
          document.body.classList.toggle('bulk-order-bars-stuck', stuck);
        }
      };

      const requestCheck = () => {
        if (framePending) return;
        framePending = true;
        window.requestAnimationFrame(check);
      };

      if ('ResizeObserver' in window) {
        this.stickyResizeObserver = new ResizeObserver(() => {
          syncGeometry();
          requestCheck();
        });
        this.stickyResizeObserver.observe(commandbar);
      } else {
        window.addEventListener('resize', () => {
          syncGeometry();
          requestCheck();
        }, { passive: true });
      }

      window.addEventListener('scroll', requestCheck, { passive: true });
      syncGeometry();
      check();
    },

    /**
     * Bind all event listeners
     */
    bindEvents: function() {
      // Product search with debouncing
      const productSearch = document.querySelector(this.config.selectors.productSearch);
      if (productSearch) {
        productSearch.addEventListener('input', (e) => this.debouncedProductSearch(e));
        // Also add keyup for immediate feedback on Enter key
        productSearch.addEventListener('keyup', (e) => {
          if (e.key === 'Enter') {
            clearTimeout(this.state.searchTimeout);
            this.handleProductSearch(e);
          }
        });
      }

      // Collection search within filters with debouncing
      const collectionSearch = document.querySelector(this.config.selectors.collectionSearch);
      if (collectionSearch) {
        collectionSearch.addEventListener('input', (e) => this.debouncedCollectionSearch(e));
      }

      // Filter popover: toggle on the trigger, close on outside click or Esc
      const filterToggle = document.querySelector(this.config.selectors.filterToggle);
      if (filterToggle) {
        filterToggle.addEventListener('click', () => this.toggleFilters());

        document.addEventListener('click', (e) => {
          if (!this.state.filtersExpanded) return;
          const filterWrap = filterToggle.closest('[data-bulk-order-filters]');
          if (filterWrap && !filterWrap.contains(e.target)) {
            this.closeFilters();
          }
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.state.filtersExpanded) {
            this.closeFilters();
            filterToggle.focus();
          }
        });
      }

      // Collection filter buttons
      document.querySelectorAll(this.config.selectors.filterButton).forEach(button => {
        button.addEventListener('click', (e) => this.handleFilterClick(e));
      });

      // Toolbar actions
      const bulkSelectAll = document.querySelector(this.config.selectors.bulkSelectAll);
      if (bulkSelectAll) {
        bulkSelectAll.addEventListener('click', () => this.bulkSelectAll());
      }

      const clearAll = document.querySelector(this.config.selectors.clearAll);
      if (clearAll) {
        clearAll.addEventListener('click', () => this.clearAll());
      }

      // Clear-all confirmation dialog
      const clearDialog = document.querySelector(this.config.selectors.clearDialog);
      if (clearDialog) {
        const clearConfirm = clearDialog.querySelector(this.config.selectors.clearConfirm);
        const clearCancel = clearDialog.querySelector(this.config.selectors.clearCancel);

        if (clearConfirm) {
          clearConfirm.addEventListener('click', () => {
            clearDialog.close();
            this.performClearAll();
          });
        }
        if (clearCancel) {
          clearCancel.addEventListener('click', () => clearDialog.close());
        }
        // Click on the backdrop (outside the body) dismisses without clearing.
        clearDialog.addEventListener('click', (e) => {
          if (e.target === clearDialog) clearDialog.close();
        });
      }

      // Action bar: expandable selected-items panel
      const selectedToggle = document.querySelector(this.config.selectors.selectedToggle);
      const selectedPanel = document.querySelector(this.config.selectors.selectedPanel);
      if (selectedToggle && selectedPanel) {
        selectedToggle.addEventListener('click', () => {
          const isOpen = !selectedPanel.hidden;
          selectedPanel.hidden = isOpen;
          selectedToggle.setAttribute('aria-expanded', String(!isOpen));
        });
      }

      // Resume dialog: continue restores the saved order, fresh discards it.
      // Esc / backdrop-click just closes — the saved order stays for later.
      const resumeDialog = document.querySelector(this.config.selectors.resumeDialog);
      if (resumeDialog) {
        const resumeContinue = resumeDialog.querySelector(this.config.selectors.resumeContinue);
        const resumeFresh = resumeDialog.querySelector(this.config.selectors.resumeFresh);

        if (resumeContinue) {
          resumeContinue.addEventListener('click', () => {
            resumeDialog.close();
            this.restoreQuantities();
          });
        }
        if (resumeFresh) {
          resumeFresh.addEventListener('click', () => {
            resumeDialog.close();
            this.clearSavedQuantities();
          });
        }
        resumeDialog.addEventListener('click', (e) => {
          if (e.target === resumeDialog) resumeDialog.close();
        });
      }

      // Guest quote dialog
      const quoteDialog = document.querySelector(this.config.selectors.quoteDialog);
      if (quoteDialog) {
        const quoteCancel = quoteDialog.querySelector(this.config.selectors.quoteCancel);

        if (quoteCancel) {
          quoteCancel.addEventListener('click', () => this.closeQuoteDialog());
        }
        quoteDialog.addEventListener('click', (e) => {
          if (e.target === quoteDialog) this.closeQuoteDialog();
        });
      }

      const exportOrder = document.querySelector(this.config.selectors.exportOrder);
      if (exportOrder) {
        exportOrder.addEventListener('click', () => this.exportToCSV());
      }

      const saveDraft = document.querySelector(this.config.selectors.saveDraft);
      if (saveDraft) {
        saveDraft.addEventListener('click', () => this.saveDraft());
      }

      const resetFilters = document.querySelector(this.config.selectors.resetFilters);
      if (resetFilters) {
        resetFilters.addEventListener('click', () => this.resetFilters());
      }

      this.bindProductControls(document);

      // Submit button
      const submitButton = document.querySelector(this.config.selectors.submitButton);
      if (submitButton) {
        submitButton.addEventListener('click', (e) => this.handleSubmit(e));
      }

      // Contact form validation
      if (!this.state.isPartner) {
        const contactFields = document.querySelectorAll(this.config.selectors.contactField);
        contactFields.forEach(field => {
          field.addEventListener('blur', (e) => this.validateContactField(e));
        });

        const contactForm = document.querySelector(this.config.selectors.contactForm);
        if (contactForm) {
          contactForm.addEventListener('submit', (e) => this.handleContactSubmit(e));
        }
      }
    },

    /**
     * Bind controls that can be replaced when a catalog collection is loaded.
     */
    bindProductControls: function(scope) {
      scope.querySelectorAll(this.config.selectors.qtyButton).forEach(button => {
        if (button.dataset.bulkOrderBound === 'true') return;
        button.dataset.bulkOrderBound = 'true';
        button.addEventListener('click', (e) => this.handleQuantityButtonClick(e));
      });

      scope.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        if (input.dataset.bulkOrderBound === 'true') return;
        input.dataset.bulkOrderBound = 'true';
        input.addEventListener('input', (e) => this.handleQuantityInput(e));
        input.addEventListener('blur', (e) => this.validateQuantityInput(e));
        input.addEventListener('keydown', (e) => this.handleQtyKeydown(e));
        input.addEventListener('change', () => this.saveQuantities());
      });
    },

    /**
     * Initialize all quantity inputs to 0
     */
    initializeQuantities: function() {
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        if (!input.value || input.value === '') {
          input.value = '0';
        }
      });
    },

    /**
     * Shopify caps Liquid pagination at 250 products. Load every remaining
     * published storefront page through the Section Rendering API, then merge
     * the rows in catalog order. Search and collection filters subsequently
     * operate against the complete active/published catalog.
     */
    ensureFullCatalogLoaded: async function() {
      const list = document.querySelector(this.config.selectors.productsList);
      if (!list) return;

      const totalPages = parseInt(list.getAttribute('data-total-pages'), 10) || 1;
      const currentPage = parseInt(list.getAttribute('data-current-page'), 10) || 1;
      if (totalPages <= 1) {
        this.state.fullCatalogLoaded = true;
        this.pruneSavedLinesToCatalog();
        this.updateCollectionCounts();
        if (this.state.draftRestored) {
          this.restoreQuantities();
        } else {
          this.updateSidebar();
        }
        this.updateVisibleProductCount(this.countVisibleProducts(), this.state.currentFilter);
        return;
      }

      const workspace = document.querySelector(this.config.selectors.workspace);
      const search = document.querySelector(this.config.selectors.productSearch);
      const filterToggle = document.querySelector(this.config.selectors.filterToggle);
      const pagination = document.querySelector('[data-pagination]');
      const loadingButtons = document.querySelectorAll(
        '.bulk-order__commandbar-actions button, .actionbar__buttons button, [data-quote-send]'
      );
      const controller = new AbortController();
      this.state.fullCatalogAbort = controller;

      // Capture the loader once, up front. The finally used to re-read
      // window.HHPageLoader, so anything that replaced or removed the global
      // during the multi-second merge — an app embed, a consent manager, an
      // A/B injector — would make the release guard fall through while the
      // hold stayed taken, pinning the overlay permanently.
      const pageLoader = window.HHPageLoader || null;
      if (pageLoader) pageLoader.hold('bulk-order-catalog');

      // Bound the WHOLE merge, not just each request. The per-request timeout
      // caps one round trip, but this runs ceil((totalPages - 1) / 3) batches
      // in sequence, so the total is a multiple of it. The page loader gives a
      // hold HOLD_WATCHDOG_MS (60s, snippets/head.liquid) before it force-drops
      // it and reveals the page — and everything disabled below is only undone
      // in the finally. If the merge could outlast that budget, the shopper
      // would be looking at an order pad that appears ready while its search,
      // filters and every action button are still disabled, with no overlay
      // left to explain why. This deadline keeps the merge strictly inside the
      // loader's budget, so the finally always wins that race.
      // Aborting with a TimeoutError rather than an AbortError matters: the
      // catch below deliberately swallows AbortError, and this failure should
      // be seen.
      const mergeDeadline = window.setTimeout(() => {
        controller.abort(new DOMException(
          'Catalog merge exceeded its budget', 'TimeoutError'
        ));
      }, this.config.catalogMergeTimeoutMs);

      // Everything from here on is inside the try, so the finally below is the
      // single exit. It previously started after this setup, leaving the
      // unguarded loadingButtons loop able to throw between hold and try.
      try {
        if (workspace) {
          workspace.classList.add('is-loading');
          workspace.setAttribute('aria-busy', 'true');
        }
        if (search) search.disabled = true;
        if (filterToggle) filterToggle.disabled = true;
        loadingButtons.forEach(button => {
          button.dataset.bulkOrderWasDisabled = String(button.disabled);
          button.disabled = true;
        });

        const pageRows = new Map();
        // Keep the live current-page rows rather than cloning them. Cloning
        // copies the "already bound" marker but not its event listeners.
        pageRows.set(
          currentPage,
          Array.from(list.querySelectorAll(this.config.selectors.productRow))
        );

        const pagesToFetch = [];
        for (let page = 1; page <= totalPages; page++) {
          if (page !== currentPage) pagesToFetch.push(page);
        }

        // Keep request pressure modest on large catalogs.
        for (let offset = 0; offset < pagesToFetch.length; offset += 3) {
          const batch = pagesToFetch.slice(offset, offset + 3);
          const results = await Promise.all(
            batch.map(page => this.fetchCatalogPage(page, controller.signal))
          );
          results.forEach((pageList, index) => {
            pageRows.set(
              batch[index],
              Array.from(pageList.querySelectorAll(this.config.selectors.productRow)).map(row => row.cloneNode(true))
            );
          });
        }

        const fragment = document.createDocumentFragment();
        for (let page = 1; page <= totalPages; page++) {
          (pageRows.get(page) || []).forEach(row => fragment.appendChild(row));
        }
        list.replaceChildren(fragment);
        list.setAttribute('data-total-pages', '1');
        list.setAttribute('data-current-page', '1');

        this.bindProductControls(list);
        this.initializeQuantities();
        this.state.fullCatalogLoaded = true;
        this.pruneSavedLinesToCatalog();
        this.updateCollectionCounts();

        if (pagination) {
          const nav = pagination.querySelector('.pagination-nav');
          if (nav) nav.remove();
        }

        if (this.state.draftRestored) {
          this.restoreQuantities();
        } else {
          this.updateSidebar();
        }

        const searchTerm = search ? search.value.trim() : '';
        if (searchTerm) {
          this.handleProductSearch({ target: search });
        } else {
          this.filterProducts(this.state.currentFilter);
        }
      } catch (error) {
        if (error && error.name !== 'AbortError') {
          console.error('Could not load the complete bulk-order catalog:', error);
          this.showMessage('error', this.t('catalog_load_error'));
        }
      } finally {
        window.clearTimeout(mergeDeadline);
        if (workspace) {
          workspace.classList.remove('is-loading');
          workspace.removeAttribute('aria-busy');
        }
        if (pageLoader) {
          pageLoader.release('bulk-order-catalog');
        }
        if (search) search.disabled = false;
        if (filterToggle) filterToggle.disabled = false;
        loadingButtons.forEach(button => {
          button.disabled = button.dataset.bulkOrderWasDisabled === 'true';
          delete button.dataset.bulkOrderWasDisabled;
        });
      }
    },

    fetchCatalogPage: function(page, signal) {
      const section = document.querySelector(this.config.selectors.section);
      const sectionRenderId = section?.getAttribute('data-section-render-id') || 'bulk-order-form';

      // Render from the localized order-form URL: this is the template that
      // owns the dynamic section instance ID. A collection URL has a different
      // template context and may not render the same configured instance.
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('section_id', sectionRenderId);
      url.searchParams.set('page', String(page));

      // Bound the request. A bare fetch has no timeout, so a connection that
      // is accepted and then goes silent — a stalled edge node, a captive
      // portal, a proxy that never answers — leaves this promise pending for
      // the life of the page. Its caller awaits it inside a try whose finally
      // releases the page-loader hold, so "pending forever" means the
      // full-screen overlay never lifts. Timing out turns that into a visible,
      // recoverable error instead.
      const timeoutController = new AbortController();
      const timer = window.setTimeout(() => {
        timeoutController.abort(new DOMException(
          `Catalog page ${page} timed out`, 'TimeoutError'
        ));
      }, this.config.catalogFetchTimeoutMs);

      // Forward the caller's signal, keeping its own reason so a real abort
      // still surfaces as AbortError and stays silent, while a timeout reads
      // as a genuine failure and reaches the shopper.
      const forwardAbort = () => timeoutController.abort(signal.reason);
      if (signal) {
        if (signal.aborted) forwardAbort();
        else signal.addEventListener('abort', forwardAbort, { once: true });
      }
      const settle = () => {
        window.clearTimeout(timer);
        if (signal) signal.removeEventListener('abort', forwardAbort);
      };

      return fetch(url.toString(), {
        signal: timeoutController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(response => {
        if (!response.ok) throw new Error(`Catalog page ${page} returned ${response.status}`);
        return response.text();
      }).then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const pageList = doc.querySelector(this.config.selectors.productsList);
        if (!pageList) throw new Error(`Catalog page ${page} did not contain product rows`);
        return pageList;
      }).then(
        value => { settle(); return value; },
        error => { settle(); throw error; }
      );
    },

    countVisibleProducts: function() {
      const productIds = new Set();
      document.querySelectorAll(this.config.selectors.productRow).forEach(row => {
        if (row.style.display !== 'none') {
          const productId = row.getAttribute('data-product-id');
          if (productId) productIds.add(productId);
        }
      });
      return productIds.size;
    },

    /**
     * Keep collection badges aligned with the orderable catalog. Shopify's
     * collection counts include products whose every variant is sold out,
     * while those products intentionally have no row in this order form.
     */
    updateCollectionCounts: function() {
      const allProducts = new Set();
      const collectionProducts = new Map();

      document.querySelectorAll(this.config.selectors.productRow).forEach(row => {
        const productId = row.getAttribute('data-product-id');
        if (!productId) return;
        allProducts.add(productId);

        const handles = (row.getAttribute('data-collections') || '').split(',').filter(Boolean);
        handles.forEach(handle => {
          if (!collectionProducts.has(handle)) collectionProducts.set(handle, new Set());
          collectionProducts.get(handle).add(productId);
        });
      });

      document.querySelectorAll(this.config.selectors.filterButton).forEach(button => {
        const handle = button.getAttribute('data-collection-filter');
        const count = handle === 'all'
          ? allProducts.size
          : (collectionProducts.get(handle)?.size || 0);
        const countElement = button.querySelector('.filter-item__count');
        if (countElement) countElement.textContent = String(count);
      });
    },

    /**
     * A saved line can become unavailable between visits. Once every catalog
     * page is present, remove any saved variant that has no orderable row.
     */
    pruneSavedLinesToCatalog: function() {
      const saved = this.readSavedQuantities();
      if (!saved) return;

      const orderableVariantIds = new Set(
        Array.from(document.querySelectorAll(this.config.selectors.productRow))
          .map(row => row.getAttribute('data-variant-id'))
          .filter(Boolean)
      );
      let changed = false;

      Object.keys(saved.quantities).forEach(variantId => {
        if (!orderableVariantIds.has(String(variantId))) {
          delete saved.quantities[variantId];
          changed = true;
        }
      });

      if (!changed) return;
      try {
        if (Object.keys(saved.quantities).length === 0) {
          localStorage.removeItem(this.state.storageKey);
        } else {
          localStorage.setItem(this.state.storageKey, JSON.stringify({
            v: 3,
            updatedAt: new Date().toISOString(),
            quantities: saved.quantities
          }));
        }
      } catch (e) {
        console.warn('Could not prune unavailable saved variants:', e);
      }
    },

    /**
     * Debounced product search
     */
    debouncedProductSearch: function(event) {
      // Clear existing timeout
      if (this.state.searchTimeout) {
        clearTimeout(this.state.searchTimeout);
      }

      // Set new timeout
      this.state.searchTimeout = setTimeout(() => {
        this.handleProductSearch(event);
      }, this.config.searchDebounceDelay);
    },

    /**
     * Lowercase and strip combining accents so an unaccented query still
     * matches. Hungarian buyers routinely type "kancso" for "kancsó", and the
     * previous exact-substring match returned zero rows for those queries.
     * NFD splits an accented letter into base + combining mark, and the range
     * below removes the marks — including the double acute on ő and ű.
     */
    // Built from an escaped string rather than a regex literal on purpose: as a
    // literal the class contents are invisible combining marks, which an editor
    // normalising the file to NFC could fuse into the bracket and silently break.
    combiningMarks: new RegExp('[\\u0300-\\u036f]', 'g'),

    foldForSearch: function(value) {
      return (value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(this.combiningMarks, '');
    },

    /**
     * Handle product search
     */
    handleProductSearch: function(event) {
      // Raw term goes to updateCatalogResults, which forwards it to Shopify's
      // search/suggest.json — that endpoint should receive what was typed.
      // Only the local row matching below is accent-folded.
      const rawTerm = event.target.value.trim();
      const searchTerm = this.foldForSearch(rawTerm);
      const productRows = document.querySelectorAll(this.config.selectors.productRow);

      const visibleProductIds = new Set();

      productRows.forEach(row => {
        // Get product data
        const productTitle = this.foldForSearch(row.querySelector('.bulk-order__product-title')?.textContent);
        const variantTitle = this.foldForSearch(row.querySelector('.bulk-order__variant-title')?.textContent);
        const skuEl = row.querySelector('.bulk-order__variant-sku');
        const sku = this.foldForSearch(skuEl?.dataset.sku || skuEl?.textContent);
        const barcode = this.foldForSearch(row.querySelector('.bulk-order__barcode')?.textContent);

        // Check if matches search
        const matchesSearch = searchTerm === '' ||
          productTitle.includes(searchTerm) ||
          variantTitle.includes(searchTerm) ||
          sku.includes(searchTerm) ||
          barcode.includes(searchTerm);

        // Check if matches current filter
        const currentFilter = this.state.currentFilter;
        let matchesFilter = true;

        if (currentFilter !== 'all') {
          const productCollections = row.getAttribute('data-collections');
          const collectionsArray = productCollections ? productCollections.split(',') : [];
          matchesFilter = collectionsArray.includes(currentFilter);
        }

        // Show/hide based on both search and filter
        if (matchesSearch && matchesFilter) {
          row.style.display = '';
          const productId = row.getAttribute('data-product-id');
          if (productId) visibleProductIds.add(productId);
        } else {
          row.style.display = 'none';
        }
      });

      this.updateVisibleProductCount(visibleProductIds.size, this.state.currentFilter);
      this.updateCatalogResults(rawTerm);
    },

    /**
     * Debounced collection search
     */
    debouncedCollectionSearch: function(event) {
      // Clear existing timeout
      if (this.state.collectionSearchTimeout) {
        clearTimeout(this.state.collectionSearchTimeout);
      }

      // Set new timeout
      this.state.collectionSearchTimeout = setTimeout(() => {
        this.handleCollectionSearch(event);
      }, this.config.searchDebounceDelay);
    },

    /**
     * Handle collection search within filters
     */
    handleCollectionSearch: function(event) {
      const searchTerm = event.target.value.toLowerCase().trim();
      const filterItems = document.querySelectorAll('.filter-list__item:not([data-collection-filter="all"])');

      filterItems.forEach(item => {
        const collectionName = item.getAttribute('data-collection-title')?.toLowerCase() || '';
        if (searchTerm === '' || collectionName.includes(searchTerm)) {
          item.style.display = '';
        } else {
          item.style.display = 'none';
        }
      });
    },

    /**
     * Toggle the filter popover (opens over the table, never pushes it down)
     */
    toggleFilters: function() {
      if (this.state.filtersExpanded) {
        this.closeFilters();
      } else {
        this.openFilters();
      }
    },

    openFilters: function() {
      const toggle = document.querySelector(this.config.selectors.filterToggle);
      const panel = document.querySelector(this.config.selectors.filterContent);
      if (!toggle || !panel) return;

      this.state.filtersExpanded = true;
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;

      // Focus the collection search for immediate typing
      const search = panel.querySelector(this.config.selectors.collectionSearch);
      if (search) search.focus();
    },

    closeFilters: function() {
      const toggle = document.querySelector(this.config.selectors.filterToggle);
      const panel = document.querySelector(this.config.selectors.filterContent);
      if (!toggle || !panel) return;

      this.state.filtersExpanded = false;
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    },

    /**
     * Handle collection filter button click
     */
    handleFilterClick: function(event) {
      const button = event.currentTarget;
      const filter = button.getAttribute('data-collection-filter');
      // Read the visible (server-localized) name rather than hardcoding English
      const buttonName = button.querySelector('.filter-item__name');
      const collectionTitle = button.getAttribute('data-collection-title')
        || (buttonName ? buttonName.textContent.trim() : '');

      // Update button states
      document.querySelectorAll(this.config.selectors.filterButton).forEach(btn => {
        btn.classList.remove('filter-list__item--active');
        btn.setAttribute('aria-pressed', 'false');
      });

      button.classList.add('filter-list__item--active');
      button.setAttribute('aria-pressed', 'true');

      // Reflect the selection on the popover trigger
      const filterCount = document.querySelector(this.config.selectors.filterCount);
      if (filterCount) {
        filterCount.textContent = collectionTitle;
      }

      // Update state
      this.state.currentFilter = filter;

      // Filter products
      this.filterProducts(filter);

      // Re-apply search if active
      const productSearch = document.querySelector(this.config.selectors.productSearch);
      if (productSearch && productSearch.value.trim() !== '') {
        this.handleProductSearch({ target: productSearch });
      }

      // Selection made — close the popover
      this.closeFilters();
    },

    /**
     * Filter products by collection
     */
    filterProducts: function(filter) {
      const productRows = document.querySelectorAll(this.config.selectors.productRow);
      const visibleProductIds = new Set();

      productRows.forEach(row => {
        if (filter === 'all') {
          row.style.display = '';
          const productId = row.getAttribute('data-product-id');
          if (productId) visibleProductIds.add(productId);
        } else {
          const productCollections = row.getAttribute('data-collections');
          const collectionsArray = productCollections ? productCollections.split(',') : [];

          if (collectionsArray.includes(filter)) {
            row.style.display = '';
            const productId = row.getAttribute('data-product-id');
            if (productId) visibleProductIds.add(productId);
          } else {
            row.style.display = 'none';
          }
        }
      });

      // Update the visible product count
      this.updateVisibleProductCount(visibleProductIds.size, filter);
    },

    /**
     * Update visible product count in pagination info
     */
    updateVisibleProductCount: function(visibleCount, filter) {
      const paginationInfo = document.querySelector('[data-pagination-info]');
      const visibleCountElement = document.querySelector('[data-visible-count]');

      if (paginationInfo && visibleCountElement) {
        visibleCountElement.textContent = visibleCount;
      }

      this.toggleEmptyState(visibleCount);
    },

    /**
     * Show or hide the empty state when no products match search + filters
     */
    toggleEmptyState: function(visibleCount) {
      const emptyState = document.querySelector(this.config.selectors.emptyState);
      if (!emptyState) return;

      if (visibleCount === 0) {
        emptyState.hidden = false;
        this.updateEmptyStatePagination();
      } else {
        emptyState.hidden = true;
      }
    },

    /**
     * When the catalogue spans more than one server page, the client-side
     * search only sees the current page. Rather than imply "no results
     * anywhere", reveal a hint plus a link to the full store search scoped
     * to the current query.
     */
    updateEmptyStatePagination: function() {
      const list = document.querySelector(this.config.selectors.productsList);
      const totalPages = list ? parseInt(list.getAttribute('data-total-pages'), 10) || 1 : 1;

      const hint = document.querySelector(this.config.selectors.emptyOtherPages);
      const storeLink = document.querySelector(this.config.selectors.emptySearchStore);
      const multiPage = totalPages > 1;

      if (hint) hint.hidden = !multiPage;
      if (storeLink) {
        storeLink.hidden = !multiPage;
        if (multiPage) {
          const search = document.querySelector(this.config.selectors.productSearch);
          const term = search ? search.value.trim() : '';
          const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
          storeLink.href = root + 'search?type=product&q=' + encodeURIComponent(term);
        }
      }
    },

    /**
     * Clear search + filters to start fresh
     */
    resetFilters: function() {
      const productSearch = document.querySelector(this.config.selectors.productSearch);
      if (productSearch) {
        productSearch.value = '';
      }

      // Reset active collection filter back to All Products
      const allFilterButton = document.querySelector(`${this.config.selectors.filterButton}[data-collection-filter="all"]`);
      if (allFilterButton) {
        allFilterButton.click();
      } else {
        this.state.currentFilter = 'all';
        this.filterProducts('all');
      }

      // Ensure product search re-applies after filter reset
      this.handleProductSearch({ target: productSearch || { value: '' } });
    },

    /**
     * Bulk select all visible products (set quantity to 1)
     */
    bulkSelectAll: function() {
      const productRows = document.querySelectorAll(this.config.selectors.productRow);
      let count = 0;

      productRows.forEach(row => {
        // Only select visible rows
        if (row.style.display !== 'none') {
          const pcsInput = row.querySelector('[data-qty-input="pcs"]');
          if (pcsInput && parseInt(pcsInput.value) === 0) {
            pcsInput.value = '1';
            this.updateRowSubtotal(pcsInput);
            count++;
          }
        }
      });

      this.updateSidebar();
      this.scheduleAutosave();
      this.showMessage('success', this.t('selected_n', count));
    },

    /**
     * Clear all quantities
     */
    clearAll: function() {
      const dialog = document.querySelector(this.config.selectors.clearDialog);

      // Prefer the accessible <dialog>; fall back to a native confirm() only
      // where showModal isn't supported.
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        return;
      }

      if (confirm(this.t('confirm_clear'))) {
        this.performClearAll();
      }
    },

    /**
     * Actually reset every quantity to zero.
     */
    performClearAll: function() {
      // Snapshot the full saved order (DOM flushed first) so clear is undoable.
      this.saveQuantities();
      this._clearUndoSnapshot = localStorage.getItem(this.state.storageKey) || null;

      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        input.value = '0';
        const row = input.closest(this.config.selectors.productRow);
        if (row) {
          const subtotalElement = row.querySelector(this.config.selectors.lineSubtotal);
          if (subtotalElement) {
            subtotalElement.textContent = this.formatMoney(0);
          }
        }
      });

      // Clear storage BEFORE recomputing totals — the totals merge in saved
      // items from other pagination pages, which must not survive a clear.
      this.clearSavedQuantities();
      this.updateSidebar();
      this.showClearedUndo();
    },

    /**
     * Export order to CSV
     */
    exportToCSV: function() {
      const items = this.getSelectedItems();

      if (items.length === 0) {
        this.showMessage('error', this.t('no_items_export'));
        return;
      }

      // Build localized CSV rows
      const headerColumns = [
        this.t('csv_product'),
        this.t('csv_variant'),
        this.t('csv_sku'),
        this.t('csv_barcode'),
        this.t('csv_qty_pcs'),
        this.t('csv_qty_cases'),
        this.t('csv_total_pieces')
      ];
      if (this.state.isPartner) {
        headerColumns.push(this.t('csv_price'), this.t('csv_subtotal'));
      }

      let csvContent = headerColumns.map(value => this.escapeCSV(value)).join(',') + '\n';

      items.forEach(item => {
        const row = [
          this.escapeCSV(item.productTitle),
          this.escapeCSV(item.variantTitle),
          this.escapeCSV(item.sku),
          this.escapeCSV(item.barcode || ''),
          item.pcsQty,
          item.kartons,
          item.quantity
        ];

        if (this.state.isPartner) {
          const priceFormatted = this.formatMoney(item.price / 100);
          const subtotal = (item.quantity * item.price) / 100;
          row.push(this.escapeCSV(priceFormatted), this.escapeCSV(this.formatMoney(subtotal)));
        }

        csvContent += row.join(',') + '\n';
      });

      // Download via a Blob URL. A data: URI (the previous approach) is capped
      // by browser URL-length limits and would truncate large orders; a Blob
      // has no such ceiling. Prepend a UTF-8 BOM so Excel reads accents correctly.
      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk_order_${new Date().getTime()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.showMessage('success', this.t('exported'));
    },

    /**
     * Escape CSV values
     */
    escapeCSV: function(value) {
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        return '"' + value.replace(/"/g, '""') + '"';
      }
      return value;
    },

    /**
     * Save draft order
     */
    saveDraft: function() {
      const items = this.getSelectedItems();

      if (items.length === 0) {
        this.showMessage('error', this.t('no_items_save'));
        return;
      }

      try {
        // Manual save uses the same customer-scoped payload as autosave and
        // resume, so the saved draft is actually the one restored later.
        if (this.saveQuantities()) {
          this.showMessage('success', this.t('draft_saved_n', items.length));
        } else {
          this.showMessage('error', this.t('could_not_save'));
        }
      } catch (e) {
        console.error('Error saving draft:', e);
        this.showMessage('error', this.t('could_not_save'));
      }
    },

    /**
     * Handle quantity button click (+ or -)
     */
    handleQuantityButtonClick: function(event) {
      const button = event.currentTarget;
      const unitType = button.getAttribute('data-qty-change'); // 'pcs' or 'karton'
      const action = button.getAttribute('data-qty-action'); // 'increase' or 'decrease'

      // Find the input in the same parent
      const parent = button.closest('.bulk-order__quantity-wrapper');
      const input = parent.querySelector(`[data-qty-input="${unitType}"]`);

      if (!input) return;

      let currentValue = parseInt(input.value) || 0;
      const step = 1;

      if (action === 'increase') {
        currentValue += step;
      } else if (action === 'decrease' && currentValue > 0) {
        currentValue -= step;
      }

      input.value = currentValue;

      // Update calculations
      this.updateRowSubtotal(input);
      this.updateSidebar();
      this.scheduleAutosave();
    },

    /**
     * Handle direct quantity input
     */
    handleQuantityInput: function(event) {
      const input = event.currentTarget;
      this.updateRowSubtotal(input);
      this.updateSidebar();
      this.scheduleAutosave();
    },

    /**
     * Spreadsheet-style entry: Enter jumps to the same quantity column in
     * the next visible row (Shift+Enter goes up). Arrow keys keep their
     * native number-input increment/decrement behaviour.
     */
    handleQtyKeydown: function(event) {
      if (event.key !== 'Enter') return;
      event.preventDefault();

      const input = event.currentTarget;
      const unitType = input.getAttribute('data-qty-input');
      const currentRow = input.closest(this.config.selectors.productRow);
      if (!currentRow) return;

      const visibleRows = Array.from(
        document.querySelectorAll(this.config.selectors.productRow)
      ).filter(row => row.style.display !== 'none');

      const index = visibleRows.indexOf(currentRow);
      const target = visibleRows[index + (event.shiftKey ? -1 : 1)];
      if (!target) return;

      const nextInput = target.querySelector(`[data-qty-input="${unitType}"]`);
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    },

    /**
     * Validate quantity input on blur
     */
    validateQuantityInput: function(event) {
      const input = event.currentTarget;
      let value = parseInt(input.value);

      if (isNaN(value) || value < 0) {
        value = 0;
      }

      input.value = value;
      this.updateSidebar();
      this.scheduleAutosave();
    },

    /**
     * Update row subtotal (for logged-in customers)
     */
    updateRowSubtotal: function(input) {
      if (!this.state.isPartner) return;

      const row = input.closest(this.config.selectors.productRow);
      if (!row) return;

      const pcsInput = row.querySelector('[data-qty-input="pcs"]');
      const kartonInput = row.querySelector('[data-qty-input="karton"]');
      const cartonQty = parseInt(pcsInput?.getAttribute('data-carton-qty')) || 0;
      const priceElement = row.querySelector('[data-variant-price]');
      const subtotalElement = row.querySelector(this.config.selectors.lineSubtotal);

      if (!pcsInput || !kartonInput || !priceElement || !subtotalElement) return;

      const pcsQuantity = parseInt(pcsInput.value) || 0;
      const kartonQuantity = parseInt(kartonInput.value) || 0;
      const totalPcs = pcsQuantity + (kartonQuantity * cartonQty);
      const priceInCents = parseInt(priceElement.getAttribute('data-variant-price')) || 0;

      const subtotal = (totalPcs * priceInCents) / 100;

      // Format as currency
      subtotalElement.textContent = this.formatMoney(subtotal);
    },

    /**
     * Update sidebar with totals and selected items
     */
    updateSidebar: function() {
      const items = this.getSelectedItems();

      // Update counts
      this.state.totalItems = items.length;
      this.state.totalPieces = items.reduce((sum, item) => sum + item.quantity, 0);
      this.state.totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.price) / 100, 0);

      // Update stat displays
      const totalItemsEl = document.querySelector(this.config.selectors.totalItems);
      if (totalItemsEl) {
        totalItemsEl.textContent = this.state.totalItems;
      }

      const totalPiecesEl = document.querySelector(this.config.selectors.totalPieces);
      if (totalPiecesEl) {
        totalPiecesEl.textContent = this.state.totalPieces;
      }

      const summaryTotalEl = document.querySelector(this.config.selectors.summaryTotal);
      if (summaryTotalEl && this.state.isPartner) {
        summaryTotalEl.textContent = this.formatMoney(this.state.totalAmount);
      }

      // Update selected items preview
      this.updateSelectedItemsPreview(items);

      // Visual emphasis: steppers with a value stand out from untouched ones
      this.updateQtyEmphasis();
    },

    /**
     * Toggle .has-qty on every stepper wrapper so filled controls render at
     * full strength while untouched ones recede (see CSS). classList.toggle
     * with an unchanged value is a no-op, so this stays cheap at 800+ inputs.
     */
    updateQtyEmphasis: function() {
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        const wrapper = input.closest('.bulk-order__quantity-wrapper');
        if (wrapper) {
          wrapper.classList.toggle('has-qty', (parseInt(input.value) || 0) > 0);
        }
      });
    },

    /**
     * Update selected items preview in sidebar
     */
    updateSelectedItemsPreview: function(items) {
      const listEl = document.querySelector(this.config.selectors.selectedItemsList);
      if (!listEl) return;

      if (items.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'items-preview__empty';
        empty.textContent = this.t('no_items_selected');
        listEl.replaceChildren(empty);
        return;
      }

      // Show max 10 items in preview
      const displayItems = items.slice(0, 10);
      const fragment = document.createDocumentFragment();
      displayItems.forEach(item => {
        const preview = document.createElement('div');
        preview.className = 'preview-item';

        if (item.imageUrl) {
          const imageWrap = document.createElement('div');
          imageWrap.className = 'preview-item__image';
          const image = document.createElement('img');
          image.src = item.imageUrl;
          image.alt = item.productTitle;
          image.width = 40;
          image.height = 40;
          imageWrap.appendChild(image);
          preview.appendChild(imageWrap);
        }

        const details = document.createElement('div');
        details.className = 'preview-item__details';
        const name = document.createElement('div');
        name.className = 'preview-item__name';
        name.textContent = item.productTitle;
        const quantity = document.createElement('div');
        quantity.className = 'preview-item__qty';
        quantity.textContent = `${item.quantity} ${this.t('pcs')}`;
        details.append(name, quantity);
        preview.appendChild(details);

        if (this.state.isPartner) {
          const price = document.createElement('span');
          price.className = 'preview-item__price';
          price.textContent = this.formatMoney((item.quantity * item.price) / 100);
          preview.appendChild(price);
        }

        fragment.appendChild(preview);
      });

      if (items.length > 10) {
        const more = document.createElement('p');
        more.className = 'items-preview__more';
        more.textContent = this.t('more_n', items.length - 10);
        fragment.appendChild(more);
      }

      listEl.replaceChildren(fragment);
    },

    /**
     * Get selected items
     */
    getSelectedItems: function() {
      const items = [];
      const domVariantIds = new Set();

      // Quantities are ORDER state, not view state: rows hidden by the
      // search/filter must still count. (Skipping them silently dropped
      // lines from totals, exports and submission whenever a filter was
      // active at submit time.)
      document.querySelectorAll(this.config.selectors.productRow).forEach(row => {
        const pcsInput = row.querySelector('[data-qty-input="pcs"]');
        const kartonInput = row.querySelector('[data-qty-input="karton"]');
        const variantId = row.getAttribute('data-variant-id');

        if (variantId) domVariantIds.add(variantId);
        if (!pcsInput || !kartonInput) return;

        const pcsQty = parseInt(pcsInput.value) || 0;
        const kartonQty = parseInt(kartonInput.value) || 0;
        const cartonSize = parseInt(pcsInput.getAttribute('data-carton-qty')) || 0;
        const totalPcs = pcsQty + (kartonQty * cartonSize);

        if (totalPcs > 0) {
          // Get product info
          const productTitle = row.querySelector('.bulk-order__product-title')?.textContent.trim() || '';
          const variantTitle = row.querySelector('.bulk-order__variant-title')?.textContent.trim() || '';
          const skuEl = row.querySelector('.bulk-order__variant-sku');
          const sku = (skuEl?.dataset.sku || skuEl?.textContent.replace('SKU: ', '') || '').trim();
          const barcode = row.querySelector('.bulk-order__barcode')?.textContent.trim() || '';
          const price = row.querySelector('[data-variant-price]')?.getAttribute('data-variant-price') || '0';
          const imageUrl = row.querySelector('.bulk-order__product-image img')?.src || '';

          items.push({
            variantId: variantId,
            productTitle: productTitle,
            variantTitle: variantTitle,
            sku: sku,
            barcode: barcode,
            quantity: totalPcs,  // Total combined quantity
            pcsQty: pcsQty,      // Individual pcs for display
            kartons: kartonQty,  // Individual kartons for display
            cartonSize: cartonSize,
            price: parseInt(price),
            imageUrl: imageUrl
          });
        }
      });

      // With a paginated catalogue only the current page's rows exist in the
      // DOM. Append saved items from OTHER pages (their display data was
      // captured into the draft when they were edited), so totals, exports
      // and submission cover the whole order — not just the visible page.
      const saved = this.state.draftRestored ? this.readSavedQuantities() : null;
      if (saved && !this.state.fullCatalogLoaded) {
        for (const variantId in saved.quantities) {
          if (domVariantIds.has(variantId)) continue;

          const entry = saved.quantities[variantId];
          const meta = entry && entry.meta;
          if (!meta) continue;

          const pcsQty = parseInt(entry.pcs) || 0;
          const kartonQty = parseInt(entry.karton) || 0;
          const cartonSize = parseInt(meta.cartonSize) || 0;
          const totalPcs = pcsQty + (kartonQty * cartonSize);
          if (totalPcs <= 0) continue;

          items.push({
            variantId: variantId,
            productTitle: meta.title || '',
            variantTitle: meta.variant || '',
            sku: meta.sku || '',
            barcode: meta.barcode || '',
            quantity: totalPcs,
            pcsQty: pcsQty,
            kartons: kartonQty,
            cartonSize: cartonSize,
            price: parseInt(meta.price) || 0,
            imageUrl: meta.image || ''
          });
        }
      }

      return items;
    },

    /**
     * Format money using Shopify's money format
     */
    formatMoney: function(amount) {
      const cents = Math.round(Number(amount || 0) * 100);
      const formatString = this.state.moneyFormat || '${{amount}}';

      const formatWithDelimiters = (price, precision = 2, thousands = ',', decimal = '.') => {
        if (isNaN(price)) return '0.00';

        // Shopify money values are ALWAYS in subunits (1/100), regardless of
        // how many decimals the display format shows — so divide by 100
        // first, THEN apply the precision. Dividing by 10^precision made
        // no-decimals formats (like this store's HUF) come out 100× too big.
        const number = (price / 100).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        const centsValue = parts[1] ? `${decimal}${parts[1]}` : '';

        return dollars + centsValue;
      };

      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholderRegex);
      const placeholder = match ? match[1] : 'amount';

      let value;

      switch (placeholder) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_currency':
          value = formatWithDelimiters(cents);
          break;
        case 'amount_no_decimals_with_currency':
          value = formatWithDelimiters(cents, 0);
          break;
        default:
          value = formatWithDelimiters(cents);
      }

      return formatString.replace(placeholderRegex, value);
    },

    /**
     * Get contact information
     */
    getContactInformation: function() {
      const contactForm = document.querySelector(this.config.selectors.contactForm);
      if (!contactForm) return null;

      return {
        company: contactForm.querySelector('[data-contact-field="company"]')?.value.trim() || '',
        name: contactForm.querySelector('[data-contact-field="name"]')?.value.trim() || '',
        email: contactForm.querySelector('[data-contact-field="email"]')?.value.trim() || '',
        phone: contactForm.querySelector('[data-contact-field="phone"]')?.value.trim() || ''
      };
    },

    /**
     * Validate contact field
     */
    validateContactField: function(event) {
      const field = event.currentTarget;
      const fieldName = field.getAttribute('data-contact-field');
      const value = field.value.trim();
      const contactForm = field.closest(this.config.selectors.contactForm);
      const errorElement = contactForm?.querySelector(`[data-error-for="${fieldName}"]`);

      let errorMessage = '';

      // Required field check
      if (!value) {
        errorMessage = this.t('field_required');
      } else {
        // Email validation
        if (fieldName === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            errorMessage = this.t('invalid_email');
          }
        }

        // Phone validation (basic)
        if (fieldName === 'phone') {
          const phoneRegex = /^[\d\s\+\-\(\)]+$/;
          if (!phoneRegex.test(value) || value.length < 10) {
            errorMessage = this.t('invalid_phone');
          }
        }
      }

      // Display error
      if (errorElement) {
        errorElement.textContent = errorMessage;
      }

      if (errorMessage) {
        field.classList.add('error');
        field.setAttribute('aria-invalid', 'true');
        return false;
      } else {
        field.classList.remove('error');
        field.removeAttribute('aria-invalid');
        return true;
      }
    },

    /**
     * Validate all contact fields
     */
    validateAllContactFields: function() {
      const contactFields = document.querySelectorAll(this.config.selectors.contactField);
      let allValid = true;
      let firstInvalid = null;

      contactFields.forEach(field => {
        const isValid = this.validateContactField({ currentTarget: field });
        if (!isValid) {
          allValid = false;
          if (!firstInvalid) firstInvalid = field;
        }
      });

      if (firstInvalid) firstInvalid.focus();
      return allValid;
    },

    openQuoteDialog: function() {
      const quoteDialog = document.querySelector(this.config.selectors.quoteDialog);
      if (!quoteDialog) return;

      if (typeof quoteDialog.showModal === 'function') {
        if (!quoteDialog.open) quoteDialog.showModal();
      } else {
        quoteDialog.setAttribute('open', '');
      }

      const firstField = quoteDialog.querySelector(this.config.selectors.contactField);
      if (firstField) firstField.focus();
    },

    closeQuoteDialog: function() {
      const quoteDialog = document.querySelector(this.config.selectors.quoteDialog);
      if (!quoteDialog) return;
      if (typeof quoteDialog.close === 'function' && quoteDialog.open) {
        quoteDialog.close();
      } else {
        quoteDialog.removeAttribute('open');
      }
    },

    /**
     * Handle form submission
     */
    handleSubmit: function(event) {
      const button = event.currentTarget;
      const action = button.getAttribute('data-action');

      // Get selected items
      const items = this.getSelectedItems();

      if (items.length === 0) {
        this.showMessage('error', this.t('select_one'));
        return;
      }

      if (action === 'add-to-cart') {
        this.handleAddToCart(items, button);
      } else if (action === 'request-quote') {
        // Contact details are collected by Shopify's native contact form,
        // preserving its spam protection and server-side error handling.
        this.openQuoteDialog();
      }
    },

    /**
     * Prepare Shopify's native contact form. Do not replace this with fetch:
     * the native submit path retains Shopify's hCaptcha/spam checks and lets
     * Liquid render a trustworthy success or server-error response.
     */
    handleContactSubmit: function(event) {
      const form = event.currentTarget;
      const items = this.getSelectedItems();

      if (items.length === 0) {
        event.preventDefault();
        this.showMessage('error', this.t('select_one'));
        return;
      }

      if (!this.validateAllContactFields()) {
        event.preventDefault();
        this.showMessage('error', this.t('fill_contact'));
        return;
      }

      const orderBody = form.querySelector(this.config.selectors.contactOrderBody);
      const contact = this.getContactInformation();
      if (!orderBody || !contact) {
        event.preventDefault();
        this.showMessage('error', this.t('quote_error'));
        return;
      }

      orderBody.value = this.buildQuoteBody(items, contact);
      this.saveQuantities();
      form.setAttribute('aria-busy', 'true');
    },

    buildQuoteBody: function(items, contact) {
      const locale = document.documentElement.lang || undefined;
      const requestedOn = new Date().toLocaleString(locale);
      const lines = [
        this.t('quote_title'),
        '',
        `${this.t('quote_contact_heading')}:`,
        `${this.t('quote_company')}: ${contact.company}`,
        `${this.t('quote_name')}: ${contact.name}`,
        `${this.t('quote_email')}: ${contact.email}`,
        `${this.t('quote_phone')}: ${contact.phone}`,
        '',
        `${this.t('quote_order_heading')}:`,
        `${this.t('quote_requested_on')}: ${requestedOn}`,
        ''
      ];

      items.forEach((item, index) => {
        lines.push(`${index + 1}. ${item.productTitle} — ${item.variantTitle}`);
        if (item.sku) lines.push(`   ${this.t('quote_sku')}: ${item.sku}`);
        if (item.barcode) lines.push(`   ${this.t('quote_barcode')}: ${item.barcode}`);
        lines.push(
          `   ${this.t('quote_quantity')}: ${item.quantity} ${this.t('pcs')} ` +
          `(${item.pcsQty} ${this.t('pcs')} + ${item.kartons} ${this.t('cases')} × ${item.cartonSize})`,
          ''
        );
      });

      return lines.join('\n');
    },

    /**
     * Interpret the Liquid result after Shopify redirects back from a contact
     * submission. Success is only reported when Shopify confirms it.
     */
    handleContactResponse: function() {
      const success = document.querySelector(this.config.selectors.contactSuccess);
      const serverErrors = document.querySelector(this.config.selectors.contactServerErrors);

      if (success) {
        this.state.orderCompleted = true;
        this.clearSavedQuantities();
        this.closeQuoteDialog();
        this.showMessage('success', this.t('quote_sent'));
        return true;
      }

      if (serverErrors) {
        this.restoreQuantities();
        this.openQuoteDialog();
        window.requestAnimationFrame(() => serverErrors.focus());
        return true;
      }

      return false;
    },

    /**
     * Handle add to cart (logged-in customers)
     */
    handleAddToCart: function(items, button) {
      // Show loading state
      this.setButtonLoading(button, true);

      // Prepare cart items for Shopify
      const cartItems = items.map(item => ({
        id: item.variantId,
        quantity: item.quantity
      }));

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      // Add to cart using Shopify Ajax API
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      })
      .then(response => response.json().then(data => ({ ok: response.ok, data: data })))
      .then(result => {
        // Shopify returns a 422 with { status, message, description } when a line
        // can't be added (out of stock, quantity rules, sold-out variant, …).
        // Without this check a rejected add was being reported as success.
        if (!result.ok) {
          const detail = result.data && (result.data.description || result.data.message);
          throw new Error(detail || 'cart_add_failed');
        }

        this.setButtonLoading(button, false);
        this.state.orderCompleted = true;
        this.clearSavedQuantities();
        this.showMessage('success', this.t('added_n', items.length));

        // Redirect to cart after a short delay
        setTimeout(() => {
          window.location.href = root + 'cart';
        }, 1500);
      })
      .catch(error => {
        this.setButtonLoading(button, false);
        console.error('Error adding to cart:', error);
        // Surface Shopify's own reason when we have one; otherwise the generic message.
        const detail = error && error.message && error.message !== 'cart_add_failed'
          ? error.message
          : this.t('cart_error');
        this.showMessage('error', detail);
      });
    },

    /**
     * Set button loading state
     */
    setButtonLoading: function(button, isLoading) {
      // `is-busy`, not `loading`. `loading` is the page-loader overlay's class
      // (snippets/loading.liquid), and its rules were unscoped, so this button
      // was inheriting position:fixed/inset:0/100vw/100vh — and, because
      // `body.hh-page-ready .loading { display: none }` outranked
      // `.btn-action { display: inline-flex }`, it disappeared entirely for the
      // length of every add-to-cart. The overlay rules are now scoped to
      // `body > .loading`, which fixes it from the other side too; this rename
      // is the root cause. Not `is-loading` either — that is a live token in
      // theme.css and would pull `.btn__text`/`.btn__loader` opacity rules onto
      // this button. `is-busy` is unused theme-wide.
      button.classList.toggle('is-busy', isLoading);
      button.disabled = isLoading;

      // Loaders are hidden via the `hidden` attribute in the markup;
      // clear any inline display left by older markup as well.
      const loader = button.querySelector('.btn__loader');
      if (loader) {
        loader.hidden = !isLoading;
        loader.style.display = isLoading ? 'inline-flex' : 'none';
      }
    },

    /**
     * Show message
     */
    showMessage: function(type, message) {
      const messagesContainer = document.querySelector(this.config.selectors.messages);
      if (!messagesContainer) return;

      messagesContainer.style.display = 'block';

      const messageElement = document.createElement('div');
      messageElement.className = `bulk-order__message bulk-order__message--${type}`;
      messageElement.setAttribute('role', type === 'error' ? 'alert' : 'status');
      messageElement.textContent = message;

      messagesContainer.innerHTML = '';
      messagesContainer.appendChild(messageElement);

      // Scroll to message
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      messageElement.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });

      // Auto-hide success messages after 5 seconds: fade out, then remove
      if (type === 'success') {
        setTimeout(() => {
          messageElement.classList.add('is-leaving');
          setTimeout(() => {
            messageElement.remove();
            if (messagesContainer.children.length === 0) {
              messagesContainer.style.display = 'none';
            }
          }, 200);
        }, 5000);
      }
    },

    /**
     * Reset form
     */
    resetForm: function() {
      // Reset all quantity inputs
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        input.value = '0';
      });

      // Reset contact form if guest
      if (!this.state.isPartner) {
        const contactForm = document.querySelector(this.config.selectors.contactForm);
        if (contactForm) {
          contactForm.reset();
          // Clear error messages
          document.querySelectorAll('.bulk-order-contact__error').forEach(error => {
            error.textContent = '';
          });
          // Remove error classes
          document.querySelectorAll('.bulk-order-contact__input').forEach(input => {
            input.classList.remove('error');
            input.removeAttribute('aria-invalid');
          });
        }
      }

      // Clear storage BEFORE recomputing totals — the totals merge in saved
      // items from other pagination pages, which must not survive a reset.
      this.clearSavedQuantities();
      this.updateSidebar();

      // Reset filter to "All"
      const allButton = document.querySelector('[data-collection-filter="all"]');
      if (allButton) {
        allButton.click();
      }

      // Clear search
      const productSearch = document.querySelector(this.config.selectors.productSearch);
      if (productSearch) {
        productSearch.value = '';
      }
    },

    /**
     * Read the auto-saved order from localStorage.
     * Accepts only the actor-scoped v3 format and expires abandoned drafts
     * after 30 days. Returns null when nothing usable remains.
     */
    readSavedQuantities: function() {
      try {
        const raw = localStorage.getItem(this.state.storageKey);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed || parsed.v !== 3) {
          localStorage.removeItem(this.state.storageKey);
          return null;
        }

        const updatedAtMs = Date.parse(parsed.updatedAt || '');
        if (!Number.isFinite(updatedAtMs) || Date.now() - updatedAtMs > this.state.storageTtlMs) {
          localStorage.removeItem(this.state.storageKey);
          return null;
        }

        const quantities = parsed.quantities;
        if (!quantities || typeof quantities !== 'object') return null;

        // Count variants with any quantity > 0
        let count = 0;
        for (const variantId in quantities) {
          const entry = quantities[variantId];
          if (entry && (parseInt(entry.pcs) > 0 || parseInt(entry.karton) > 0)) {
            count++;
          }
        }
        if (count === 0) return null;

        return {
          quantities: quantities,
          count: count,
          updatedAt: parsed.updatedAt
        };
      } catch (e) {
        console.warn('Could not read saved quantities:', e);
        return null;
      }
    },

    /**
     * Auto-save quantities to localStorage.
     * MERGES with what's already saved: with a paginated catalogue only the
     * current page's rows exist in the DOM, so rebuilding from scratch would
     * silently wipe quantities entered on other pages. Merging is gated on
     * `draftRestored`: if the user dismissed the resume dialog and starts a
     * NEW order, the old draft is replaced rather than blended in.
     * Each line also captures its display data (title, price, carton size…)
     * so off-page items can appear in totals, previews and submissions.
     */
    saveQuantities: function() {
      const saved = this.state.draftRestored ? this.readSavedQuantities() : null;
      const quantities = saved ? saved.quantities : {};

      document.querySelectorAll(this.config.selectors.productRow).forEach(row => {
        const variantId = row.getAttribute('data-variant-id');
        if (!variantId) return;

        const pcs = parseInt(row.querySelector('[data-qty-input="pcs"]')?.value) || 0;
        const karton = parseInt(row.querySelector('[data-qty-input="karton"]')?.value) || 0;

        if (pcs <= 0 && karton <= 0) {
          delete quantities[variantId];
          return;
        }

        const skuEl = row.querySelector('.bulk-order__variant-sku');
        const pcsInput = row.querySelector('[data-qty-input="pcs"]');

        const entry = {};
        if (pcs > 0) entry.pcs = String(pcs);
        if (karton > 0) entry.karton = String(karton);
        entry.meta = {
          title: row.querySelector('.bulk-order__product-title')?.textContent.trim() || '',
          variant: row.querySelector('.bulk-order__variant-title')?.textContent.trim() || '',
          sku: (skuEl?.dataset.sku || '').trim(),
          barcode: row.querySelector('.bulk-order__barcode')?.textContent.trim() || '',
          price: row.querySelector('[data-variant-price]')?.getAttribute('data-variant-price') || '0',
          image: row.querySelector('.bulk-order__product-image img')?.src || '',
          cartonSize: pcsInput?.getAttribute('data-carton-qty') || '0'
        };

        quantities[variantId] = entry;
      });

      let savedSuccessfully = true;
      try {
        if (Object.keys(quantities).length === 0) {
          localStorage.removeItem(this.state.storageKey);
        } else {
          localStorage.setItem(this.state.storageKey, JSON.stringify({
            v: 3,
            updatedAt: new Date().toISOString(),
            quantities: quantities
          }));
        }
      } catch (e) {
        savedSuccessfully = false;
        console.warn('Could not save quantities to localStorage:', e);
      }

      // From here on this draft is the live order — subsequent saves merge
      this.state.draftRestored = true;
      return savedSuccessfully;
    },

    /**
     * Debounced auto-save — called on every quantity change (stepper clicks
     * included, which never fire the input's native `change` event).
     */
    scheduleAutosave: function() {
      // A fresh user edit means a new order is in progress again
      this.state.orderCompleted = false;

      if (this.state.autosaveTimeout) {
        clearTimeout(this.state.autosaveTimeout);
      }
      this.state.autosaveTimeout = setTimeout(() => {
        this.saveQuantities();
      }, 400);
    },

    /**
     * Apply the saved order to the form (current page's rows).
     */
    restoreQuantities: function() {
      const saved = this.readSavedQuantities();
      if (!saved) return;

      this.state.draftRestored = true;

      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        const variantId = input.getAttribute('data-variant-id');
        const unitType = input.getAttribute('data-qty-input');

        if (variantId && saved.quantities[variantId] && saved.quantities[variantId][unitType]) {
          input.value = saved.quantities[variantId][unitType];
        }
      });

      // Update all row subtotals and sidebar
      document.querySelectorAll(this.config.selectors.qtyInput).forEach(input => {
        if (parseInt(input.value) > 0) {
          this.updateRowSubtotal(input);
        }
      });
      this.updateSidebar();
    },

    /**
     * On load: when an unfinished order exists, ask before restoring it.
     * Continue → restore; start fresh → discard the saved order.
     * Dismissing the dialog (Esc / backdrop) keeps the saved order for later
     * without applying it. Falls back to silent restore without <dialog>.
     */
    maybeOfferResume: function() {
      const saved = this.readSavedQuantities();
      if (!saved) return;

      const dialog = document.querySelector(this.config.selectors.resumeDialog);
      if (!dialog || typeof dialog.showModal !== 'function') {
        this.restoreQuantities();
        return;
      }

      const body = dialog.querySelector(this.config.selectors.resumeBody);
      if (body) {
        body.textContent = this.t('resume_n', saved.count);
      }

      // Returning mid-session (e.g. pagination) shouldn't re-ask: if the URL
      // carries a page param, the user is actively working — just restore.
      if (window.location.search.includes('page=')) {
        this.restoreQuantities();
        return;
      }

      dialog.showModal();
    },

    /**
     * Clear saved quantities from localStorage
     */
    clearSavedQuantities: function() {
      // Cancel any pending debounced save so it can't resurrect the draft
      // right after a successful submit or an explicit discard.
      if (this.state.autosaveTimeout) {
        clearTimeout(this.state.autosaveTimeout);
        this.state.autosaveTimeout = null;
      }

      try {
        localStorage.removeItem(this.state.storageKey);
      } catch (e) {
        console.warn('Could not clear saved quantities:', e);
      }
      this.state.draftRestored = false;
    },

    /**
     * Bind pagination events to save state
     */
    bindPaginationEvents: function() {
      // Save quantities before navigating to another page
      document.querySelectorAll('.pagination-link').forEach(link => {
        link.addEventListener('click', () => {
          this.saveQuantities();
        });
      });

      // Flush on tab close / navigation so an interrupted session never loses
      // the last change (the debounced save may still be pending). Skipped
      // once the order was successfully submitted.
      window.addEventListener('pagehide', () => {
        if (!this.state.orderCompleted) {
          this.saveQuantities();
        }
      });

      // Scroll to top when pagination loads (smooth scroll)
      const paginationSection = document.querySelector(this.config.selectors.section);
      if (paginationSection && window.location.search.includes('page=')) {
        setTimeout(() => {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          paginationSection.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }, 100);
      }
    },

    /* =========================================================================
       Reorder (partners): reload a previous order's lines into the pad.
       Lines are Liquid-rendered JSON on the dialog buttons; quantities merge
       into the saved order via the same storage path drafts use, so off-page
       lines keep counting in totals/exports/submission (order-state invariant).
       ========================================================================= */

    bindReorder: function() {
      const openBtn = document.querySelector(this.config.selectors.reorderOpen);
      const dialog = document.querySelector(this.config.selectors.reorderDialog);
      if (!openBtn || !dialog || typeof dialog.showModal !== 'function') return;

      openBtn.addEventListener('click', () => dialog.showModal());
      dialog.querySelector('[data-reorder-cancel]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });

      dialog.querySelectorAll('[data-reorder-load]').forEach(btn => {
        btn.addEventListener('click', () => {
          let lines;
          try { lines = JSON.parse(btn.getAttribute('data-lines') || '[]'); }
          catch (e) { this.showMessage('error', this.t('catalog_error')); return; }
          const applied = this.applyLines(lines);
          dialog.close();
          this.showMessage('success', this.t('reorder_loaded_n', applied));
        });
      });
    },

    /**
     * Merge external lines ({id, qty, title, variant, sku, price, image}) into
     * the saved order, then re-render. Quantities ADD to existing pcs.
     * Returns the number of lines applied.
     */
    applyLines: function(lines) {
      if (!Array.isArray(lines) || lines.length === 0) return 0;
      // Same gate as saveQuantities: a draft the user declined to resume must
      // not silently blend into the new order once draftRestored flips on.
      const saved = this.state.draftRestored ? this.readSavedQuantities() : null;
      const quantities = saved ? saved.quantities : {};
      let applied = 0;

      lines.forEach(l => {
        const qty = parseInt(l && l.qty) || 0;
        if (!l || !l.id || qty <= 0) return;
        const prev = quantities[l.id] || {};
        const entry = { pcs: String((parseInt(prev.pcs) || 0) + qty) };
        if (prev.karton) entry.karton = prev.karton;
        entry.meta = prev.meta || {
          title: l.title || '',
          variant: l.variant || '',
          sku: l.sku || '',
          barcode: l.barcode || '',
          price: String(l.price || '0'),
          image: l.image || '',
          cartonSize: String(l.cartonSize || '0')
        };
        quantities[l.id] = entry;
        applied++;
      });

      if (applied === 0) return 0;
      try {
        localStorage.setItem(this.state.storageKey, JSON.stringify({
          v: 3, updatedAt: new Date().toISOString(), quantities: quantities
        }));
      } catch (e) { console.warn('Could not persist lines:', e); return 0; }

      this.state.draftRestored = true;
      this.state.orderCompleted = false;
      this.restoreQuantities();
      this.updateSidebar();
      return applied;
    },

    /* =========================================================================
       Cross-catalog quick search: the local filter only sees the current
       pagination page, so matching products from the REST of the catalog are
       fetched via Shopify's Predictive Search API and offered as addable
       lines (added through applyLines → same order-state machinery).
       ========================================================================= */

    updateCatalogResults: function(term) {
      const panel = document.querySelector(this.config.selectors.catalogResults);
      if (!panel) return;
      if (this.state.fullCatalogLoaded) {
        panel.hidden = true;
        panel.innerHTML = '';
        return;
      }
      if (!term || term.length < 2) { panel.hidden = true; panel.innerHTML = ''; return; }

      if (this.state.catalogAbort) this.state.catalogAbort.abort();
      const ctrl = new AbortController();
      this.state.catalogAbort = ctrl;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      fetch(root + 'search/suggest.json?q=' + encodeURIComponent(term) +
            '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode',
            { signal: ctrl.signal })
        .then(r => r.json())
        .then(data => {
          const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          const onPage = new Set(
            Array.from(document.querySelectorAll(this.config.selectors.productRow))
              .map(r => r.getAttribute('data-product-handle')).filter(Boolean)
          );
          this.renderCatalogResults(products.filter(p => !onPage.has(p.handle)), panel);
        })
        .catch(() => {});
    },

    renderCatalogResults: function(products, panel) {
      panel.innerHTML = '';
      if (!products.length) { panel.hidden = true; return; }

      // The empty state's "quick search only covers this page" hint is
      // superseded when the cross-catalog matches are right below it.
      const hint = document.querySelector(this.config.selectors.emptyOtherPages);
      const storeLink = document.querySelector(this.config.selectors.emptySearchStore);
      if (hint) hint.hidden = true;
      if (storeLink) storeLink.hidden = true;

      const heading = document.createElement('h4');
      heading.className = 'bulk-order__catalog-heading';
      heading.textContent = this.t('catalog_heading');
      panel.appendChild(heading);

      const list = document.createElement('ul');
      list.className = 'bulk-order__catalog-list';

      products.forEach(p => {
        const li = document.createElement('li');
        li.className = 'bulk-order__catalog-item';

        if (p.image) {
          const img = document.createElement('img');
          img.src = p.image; img.alt = ''; img.width = 40; img.height = 40; img.loading = 'lazy';
          li.appendChild(img);
        }

        const title = document.createElement('span');
        title.className = 'bulk-order__catalog-title';
        title.textContent = p.title;
        li.appendChild(title);

        const qty = document.createElement('input');
        qty.type = 'number'; qty.min = '0'; qty.value = '12'; qty.inputMode = 'numeric';
        qty.className = 'bulk-order__catalog-qty';
        qty.setAttribute('aria-label', this.t('pcs'));
        li.appendChild(qty);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn-action btn-action--primary bulk-order__catalog-add';
        btn.textContent = this.t('catalog_add');
        btn.addEventListener('click', () => this.addFromCatalog(p.handle, qty, btn, li));
        li.appendChild(btn);

        list.appendChild(li);
      });

      panel.appendChild(list);
      panel.hidden = false;
    },

    addFromCatalog: function(handle, qtyInput, btn, li) {
      const qty = parseInt(qtyInput.value) || 0;
      if (qty <= 0) { qtyInput.focus(); return; }
      btn.disabled = true;
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      fetch(root + 'products/' + handle + '.js')
        .then(r => r.json())
        .then(product => {
          const variants = ((product && product.variants) || []).filter(v => v.available);
          if (!variants.length) throw new Error('no variants');

          if (variants.length === 1) {
            const v = variants[0];
            this.applyLines([{
              id: String(v.id), qty: qty, title: product.title,
              variant: v.title === 'Default Title' ? '' : v.title,
              sku: v.sku || '', barcode: v.barcode || '', price: String(v.price || 0),
              image: (product.featured_image || ''),
            }]);
            btn.textContent = this.t('catalog_added');
            setTimeout(() => li.remove(), 1200);
            return;
          }

          // Multi-variant: swap the row into a variant chooser
          li.querySelectorAll('.bulk-order__catalog-qty, .bulk-order__catalog-add').forEach(el => el.remove());
          const sub = document.createElement('ul');
          sub.className = 'bulk-order__catalog-variants';
          variants.forEach(v => {
            const vli = document.createElement('li');
            const label = document.createElement('span');
            label.textContent = v.title + (v.sku ? ' · ' + v.sku : '');
            vli.appendChild(label);
            const vqty = qtyInput.cloneNode(); vqty.value = String(qty);
            vli.appendChild(vqty);
            const vbtn = document.createElement('button');
            vbtn.type = 'button'; vbtn.className = 'btn-action btn-action--primary bulk-order__catalog-add';
            vbtn.textContent = this.t('catalog_add');
            vbtn.addEventListener('click', () => {
              const q = parseInt(vqty.value) || 0;
              if (q <= 0) { vqty.focus(); return; }
              this.applyLines([{
                id: String(v.id), qty: q, title: product.title,
                variant: v.title, sku: v.sku || '', barcode: v.barcode || '',
                price: String(v.price || 0),
                image: (product.featured_image || ''),
              }]);
              vbtn.textContent = this.t('catalog_added');
              vbtn.disabled = true;
            });
            vli.appendChild(vbtn);
            sub.appendChild(vli);
          });
          li.appendChild(sub);
        })
        .catch(() => {
          btn.disabled = false;
          this.showMessage('error', this.t('catalog_error'));
        });
    },

    /* =========================================================================
       Undo for clear-all: the snapshot taken in performClearAll is restored
       through the normal storage → restore path.
       ========================================================================= */

    showClearedUndo: function() {
      const container = document.querySelector(this.config.selectors.messages);
      if (!container) return;
      container.style.display = 'block';

      const el = document.createElement('div');
      el.className = 'bulk-order__message bulk-order__message--success';
      const span = document.createElement('span');
      span.textContent = this.t('all_cleared') + ' ';
      el.appendChild(span);

      if (this._clearUndoSnapshot) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bulk-order__undo-btn';
        btn.textContent = this.t('undo');
        btn.addEventListener('click', () => { this.undoClearAll(); el.remove(); });
        el.appendChild(btn);
      }

      container.innerHTML = '';
      container.appendChild(el);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
      setTimeout(() => {
        el.classList.add('is-leaving');
        setTimeout(() => el.remove(), 400);
      }, 10000);
    },

    undoClearAll: function() {
      if (!this._clearUndoSnapshot) return;
      try { localStorage.setItem(this.state.storageKey, this._clearUndoSnapshot); }
      catch (e) { return; }
      this._clearUndoSnapshot = null;
      this.state.draftRestored = true;
      this.restoreQuantities();
      this.updateSidebar();
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      BulkOrderForm.init();
    });
  } else {
    BulkOrderForm.init();
  }

  // Export to window for debugging
  window.BulkOrderForm = BulkOrderForm;

})();

/**
 * Image quick view — hover magnifier (fine pointers) + click/tap lightbox.
 * Thumbnails opt in via [data-image-zoom] (see bulk-order-product-row.liquid);
 * the no-image logo placeholder never gets the attribute.
 * NOTE: these document-level listeners register BEFORE BulkOrderForm's (which
 * bind at init), so the Escape handler uses stopImmediatePropagation to keep
 * the section's own Esc handling from also firing while the lightbox is open.
 */
(function() {
  'use strict';

  const SEL = '[data-image-zoom]';
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const POPOVER_SIZE = 340; // keep in sync with .bulk-order__image-popover CSS
  let popover = null;
  let popoverThumb = null;
  let popoverRequest = 0;
  let lightbox = null;
  let lastFocus = null;

  function t(key, fallback) {
    const bo = window.BulkOrderForm;
    return (bo && bo.state && bo.state.strings && bo.state.strings[key]) || fallback;
  }

  function rowCaption(thumb) {
    const row = thumb.closest('[data-product-row]');
    if (!row) return '';
    const title = row.querySelector('.bulk-order__product-title');
    const variant = row.querySelector('.bulk-order__variant-title');
    const parts = [];
    if (title && title.textContent.trim()) parts.push(title.textContent.trim());
    if (variant && variant.textContent.trim()) parts.push(variant.textContent.trim());
    return parts.join(' — ');
  }

  /* ---- Hover magnifier popover ---- */
  function showPopover(thumb) {
    if (!popover) {
      popover = document.createElement('div');
      popover.className = 'bulk-order__image-popover';
      popover.setAttribute('aria-hidden', 'true');
      document.body.appendChild(popover);
    }

    // `mouseover` also fires while moving between descendants of one
    // thumbnail. Do not restart the same preview in that case.
    if (popoverThumb === thumb && popover.classList.contains('is-visible')) return;

    const src = thumb.getAttribute('data-image-zoom');
    if (!src) return;

    // A reused <img> keeps painting its previous bitmap until the next source
    // finishes loading. Replace it before starting this hover's request so a
    // slow product image can never show the product hovered previously.
    const request = ++popoverRequest;
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    popoverThumb = thumb;
    popover.replaceChildren(img);
    popover.classList.add('is-loading');

    const r = thumb.getBoundingClientRect();
    const margin = 12;
    let left = r.right + margin;
    if (left + POPOVER_SIZE > window.innerWidth - margin) {
      left = Math.max(margin, r.left - margin - POPOVER_SIZE);
    }
    let top = r.top + r.height / 2 - POPOVER_SIZE / 2;
    top = Math.min(Math.max(margin, top), window.innerHeight - POPOVER_SIZE - margin);
    popover.style.left = left + 'px';
    popover.style.top = top + 'px';
    popover.classList.add('is-visible');

    img.addEventListener('load', function() {
      if (request !== popoverRequest || popoverThumb !== thumb) return;
      img.classList.add('is-loaded');
      popover.classList.remove('is-loading');
    }, { once: true });
    img.addEventListener('error', function() {
      if (request === popoverRequest && popoverThumb === thumb) hidePopover();
    }, { once: true });

    // Assigning src last ensures the 900px preview is requested only after
    // its thumbnail has actually been hovered.
    img.src = src;
  }

  function hidePopover() {
    popoverRequest++;
    popoverThumb = null;
    if (!popover) return;
    popover.classList.remove('is-visible', 'is-loading');
    // Clearing the element prevents a late response from becoming visible
    // after the pointer has already moved to a different product.
    popover.replaceChildren();
  }

  /* ---- Lightbox ---- */
  function openLightbox(thumb) {
    closeLightbox();
    lastFocus = thumb;
    const caption = rowCaption(thumb);

    lightbox = document.createElement('div');
    lightbox.className = 'bulk-order__image-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', caption || t('image_zoom', 'Kép nagyítása'));

    const fig = document.createElement('figure');
    fig.className = 'bulk-order__lightbox-figure';

    const img = document.createElement('img');
    const src = thumb.getAttribute('data-image-zoom');
    const hi = thumb.getAttribute('data-image-zoom-hi');
    img.src = src;
    if (hi) img.srcset = src + ' 1x, ' + hi + ' 2x';
    img.alt = caption;
    fig.appendChild(img);

    if (caption) {
      const cap = document.createElement('figcaption');
      cap.className = 'bulk-order__lightbox-caption';
      cap.textContent = caption;
      fig.appendChild(cap);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'bulk-order__lightbox-close';
    close.setAttribute('aria-label', t('image_close', 'Bezárás'));
    close.innerHTML = '&times;';
    close.addEventListener('click', closeLightbox);

    lightbox.appendChild(fig);
    lightbox.appendChild(close);
    lightbox.addEventListener('click', function(e) {
      if (e.target === lightbox) closeLightbox();
    });

    document.body.appendChild(lightbox);
    document.body.classList.add('bulk-order-lightbox-open');
    close.focus();
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.remove();
    lightbox = null;
    document.body.classList.remove('bulk-order-lightbox-open');
    if (lastFocus) {
      try { lastFocus.focus(); } catch (e) { /* detached row */ }
      lastFocus = null;
    }
  }

  /* ---- Delegated events ---- */
  document.addEventListener('click', function(e) {
    const thumb = e.target.closest && e.target.closest(SEL);
    if (!thumb) return;
    e.preventDefault();
    hidePopover();
    openLightbox(thumb);
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && lightbox) {
      e.stopImmediatePropagation();
      closeLightbox();
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && e.target.closest && e.target.closest(SEL)) {
      e.preventDefault();
      openLightbox(e.target.closest(SEL));
    }
  });

  document.addEventListener('mouseover', function(e) {
    if (!canHover.matches || lightbox) return;
    const thumb = e.target.closest && e.target.closest(SEL);
    if (thumb) showPopover(thumb);
  });

  document.addEventListener('mouseout', function(e) {
    const thumb = e.target.closest && e.target.closest(SEL);
    if (thumb && !(e.relatedTarget && thumb.contains(e.relatedTarget))) hidePopover();
  });

  window.addEventListener('scroll', hidePopover, { passive: true });
})();
